/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import React from 'react';
import moment from 'moment';
import _isNil from 'lodash/isNil';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from '../../CalendarSidebarTab';
import { CalendarEventType } from '../../CalendarEventType';

interface UseCalendarEventsParams {
  administrationCalendar: boolean;
  activeTab?: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum;
  checkedPositions?: string[];
  calendarCurrentView: string;
  groupEventEnabled?: boolean;
  activeDate: Date;
  calendarPresenter: any;
  calendarGroupEventsPresenter: any;
  googleCalendarEvent: any[];
  microsoftCalendarEvent: any[];
}

export const useCalendarEvents = ({
  administrationCalendar,
  activeTab,
  checkedPositions,
  calendarCurrentView,
  groupEventEnabled,
  activeDate,
  calendarPresenter,
  calendarGroupEventsPresenter,
  googleCalendarEvent,
  microsoftCalendarEvent,
}: UseCalendarEventsParams) => {
  const listFormattedDayEvents = React.useMemo(
    () =>
      groupEventEnabled && !administrationCalendar
        ? [
            ...calendarPresenter.listFormattedBookingForCalendar(),
            ...calendarGroupEventsPresenter.listGroupEventByDate(activeDate),
          ]
        : [...calendarPresenter.listFormattedBookingForCalendar()],
    [administrationCalendar, calendarPresenter, calendarGroupEventsPresenter, activeDate, groupEventEnabled],
  );

  const listFormattedWeekEvents = React.useMemo(
    () =>
      groupEventEnabled && !administrationCalendar
        ? [
            ...calendarPresenter.listFormattedBookingForCalendar(),
            ...calendarGroupEventsPresenter.listFormattedGroupEventsForCalendarWeekView(),
          ]
        : [...calendarPresenter.listFormattedBookingForCalendar()],
    [administrationCalendar, calendarPresenter, calendarGroupEventsPresenter, groupEventEnabled],
  );

  const listFormattedMonthEvents = React.useMemo(
    () =>
      groupEventEnabled && !administrationCalendar
        ? [
            ...calendarPresenter.listFormattedBookingForCalendar(),
            ...calendarGroupEventsPresenter.listFormattedGroupEventsForCalendarMonthView(),
          ]
        : [...calendarPresenter.listFormattedBookingForCalendar()],
    [administrationCalendar, calendarPresenter, calendarGroupEventsPresenter, groupEventEnabled],
  );

  const displayCurrentEventsViaView = React.useCallback(() => {
    if (calendarCurrentView === 'day-view') return listFormattedDayEvents;

    if (calendarCurrentView === 'week-view') return listFormattedWeekEvents;

    if (calendarCurrentView === 'month-view') return listFormattedMonthEvents;

    return listFormattedWeekEvents;
  }, [calendarCurrentView, listFormattedDayEvents, listFormattedWeekEvents, listFormattedMonthEvents]);

  const visibleEvents = React.useMemo(() => {
    if (!_isNil(activeTab)) {
      if (activeTab === CalendarSidebarTabTypeEnum.RECRUITERS) {
        return displayCurrentEventsViaView();
      }

      if (!_isNil(checkedPositions)) {
        return calendarPresenter.listFormattedAvailableBookingWithPositionForCalendar(checkedPositions);
      }

      return [];
    }

    return displayCurrentEventsViaView();
  }, [activeTab, checkedPositions, calendarPresenter, displayCurrentEventsViaView]);

  const allEvents: CalendarEventType[] = React.useMemo(
    () => (!administrationCalendar ? [...visibleEvents, ...googleCalendarEvent, ...microsoftCalendarEvent] : visibleEvents),
    [administrationCalendar, visibleEvents, googleCalendarEvent, microsoftCalendarEvent],
  );

  const normalizedEvents = React.useMemo(
    () =>
      allEvents.map((event) => ({
        ...event,
        end: event?.isAllDay ? moment(event.end).subtract(1, 'day').toDate() : event.end,
      })),
    [allEvents],
  );

  const hasAnyAllDayEvent = React.useMemo(() => allEvents.some((event) => event.isAllDay), [allEvents]);

  return {
    events: normalizedEvents,
    hasAnyAllDayEvent,
  };
};
